import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, X, Clock, Trash2, ChevronRight, ExternalLink, Upload, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${BACKEND_URL}${url}`;

const BillManagement = ({ userRole }) => {
  const [tenants, setTenants] = useState([]);
  const [bills, setBills] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedProofImage, setSelectedProofImage] = useState('');
  const [showProofModal, setShowProofModal] = useState(false);
  const [proofImgUrl, setProofImgUrl] = useState(null);
  const [proofImgLoading, setProofImgLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentFile, setPaymentFile] = useState(null);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  
  const [billForm, setBillForm] = useState({
    period: '',
    rent_amount: '',
    laundry_amount: '',
    parking_amount: '',
    other_amount: '',
    other_description: '',
    due_date: ''
  });

  const [modalProperty, setModalProperty] = useState('');
  const [modalPeriodMonth, setModalPeriodMonth] = useState('');
  const [modalPeriodYear, setModalPeriodYear] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setShowPaymentForm(false);
    setPaymentDate('');
    setPaymentFile(null);
    setProofImgUrl(null);
    if (!selectedBill?.proof?.id) return;
    setProofImgLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${BACKEND_URL}/api/admin/payment-proofs/${encodeURIComponent(selectedBill.proof.id)}/image`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setProofImgUrl(r.data.proof_image_url || null))
      .catch(() => setProofImgUrl(null))
      .finally(() => setProofImgLoading(false));
  }, [selectedBill]);

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!paymentDate || !paymentFile || !selectedBill) {
      setError('Tanggal dan bukti pembayaran wajib diisi');
      return;
    }
    setSubmittingPayment(true);
    setError('');
    try {
      const form = new FormData();
      form.append('payment_date', paymentDate);
      form.append('file', paymentFile);
      await axios.post(
        `${BACKEND_URL}/api/admin/bills/${encodeURIComponent(selectedBill.created_at)}/payment-proof`,
        form,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' } }
      );
      setSelectedBill(null);
      await fetchData();
      setSuccess('Pembayaran dikirim ke Verifikasi Super Admin. Transaksi dicatat setelah disetujui.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal mengirim pembayaran untuk verifikasi');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [tenantsRes, billsRes, proofsRes, propertiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/active-tenants`, config),
        axios.get(`${BACKEND_URL}/api/admin/bills`, config),
        axios.get(`${BACKEND_URL}/api/admin/payment-proofs`, config),
        axios.get(`${BACKEND_URL}/api/staff/properties`, config)
      ]);
      
      setTenants(tenantsRes.data);
      setBills(billsRes.data);
      setProofs(proofsRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBill = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!selectedTenant) {
      setError('Pilih tenant terlebih dahulu');
      return;
    }

    if (!billForm.period) {
      setError('Pilih periode tagihan (bulan & tahun)');
      return;
    }
    
    const total = parseFloat(billForm.rent_amount || 0) + 
                  parseFloat(billForm.laundry_amount || 0) + 
                  parseFloat(billForm.parking_amount || 0) + 
                  parseFloat(billForm.other_amount || 0);
    
    if (total === 0) {
      setError('Total tagihan tidak boleh Rp 0');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${BACKEND_URL}/api/admin/bills`, {
        tenant_phone: selectedTenant.tenant_phone,
        property_name: selectedTenant.property_name,
        room_number: selectedTenant.room_number,
        period: formatPeriodValue(billForm.period),
        rent_amount: parseFloat(billForm.rent_amount || 0),
        laundry_amount: parseFloat(billForm.laundry_amount || 0),
        parking_amount: parseFloat(billForm.parking_amount || 0),
        other_amount: parseFloat(billForm.other_amount || 0),
        other_description: billForm.other_description,
        due_date: billForm.due_date
      }, config);
      
      setSuccess('Tagihan berhasil dibuat!');
      setShowModal(false);
      resetForm();
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal membuat tagihan');
    }
  };

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Batalkan tagihan ini? Tenant akan bisa ditagih ulang untuk periode yang sama.')) return;
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${BACKEND_URL}/api/admin/bills/${encodeURIComponent(billId)}`, config);
      await fetchData();
      setSuccess('Tagihan berhasil dibatalkan. Tenant dapat ditagih ulang.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Gagal membatalkan tagihan');
    }
  };

  const resetForm = () => {
    setSelectedTenant(null);
    setModalProperty('');
    setModalPeriodMonth('');
    setModalPeriodYear('');
    setBillForm({
      period: '',
      rent_amount: '',
      laundry_amount: '',
      parking_amount: '',
      other_amount: '',
      other_description: '',
      due_date: ''
    });
  };

  const sortByRoomNumber = (a, b) => {
    const parse = (r) => r.replace(/(\d+)/g, (n) => n.padStart(6, '0'));
    return parse(a.room_number).localeCompare(parse(b.room_number));
  };

  const monthNameToNumber = {
    Januari: '01',
    Februari: '02',
    Maret: '03',
    April: '04',
    Mei: '05',
    Juni: '06',
    Juli: '07',
    Agustus: '08',
    September: '09',
    Oktober: '10',
    November: '11',
    Desember: '12'
  };

  const getBillMonthKey = (bill) => {
    if (bill?.period) {
      const [monthName, year] = bill.period.split(' ');
      if (monthNameToNumber[monthName] && year) {
        return `${year}-${monthNameToNumber[monthName]}`;
      }
    }
    return bill?.due_date?.slice(0, 7) || '';
  };

  const modalPeriodKey = modalPeriodYear && modalPeriodMonth ? `${modalPeriodYear}-${modalPeriodMonth}` : '';

  const tenantNameByPhone = Object.fromEntries(tenants.map(t => [t.tenant_phone, t.tenant_name]));

  const billedPhonesByPeriod = new Set(
    bills
      .filter(b => getBillMonthKey(b) === modalPeriodKey && b.status !== 'cancelled')
      .map(b => b.tenant_phone)
  );

  const availableTenantsForBill = tenants
    .filter(t => t.property_name === modalProperty && !billedPhonesByPeriod.has(t.tenant_phone))
    .sort(sortByRoomNumber);

  const openCreateModal = (tenant = null) => {
    resetForm();
    if (tenant) {
      setSelectedTenant(tenant);
      setBillForm(prev => ({
        ...prev,
        rent_amount: tenant.room_price?.toString() || '',
        laundry_amount: tenant.laundry && tenant.laundry_price > 0 ? tenant.laundry_price.toString() : '',
        parking_amount: tenant.parkir && tenant.parkir_price > 0 ? tenant.parkir_price.toString() : '',
        other_amount: tenant.cleaning && tenant.cleaning_price > 0 ? tenant.cleaning_price.toString() : '',
        other_description: tenant.cleaning ? 'Kebersihan Kamar' : '',
      }));
    }
    setShowModal(true);
    setError('');
  };

  const calculateTotal = () => {
    return parseFloat(billForm.rent_amount || 0) + 
           parseFloat(billForm.laundry_amount || 0) + 
           parseFloat(billForm.parking_amount || 0) + 
           parseFloat(billForm.other_amount || 0);
  };

  const formatPeriodValue = (value) => {
    if (!value) return '';
    const [year, month] = value.split('-');
    if (!year || !month) return value;
    const monthIndex = parseInt(month, 10) - 1;
    const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    if (monthIndex < 0 || monthIndex > 11) return value;
    return `${monthNames[monthIndex]} ${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const yearOptions = Array.from({ length: 6 }, (_, i) => (new Date().getFullYear() - 1 + i).toString());
  const monthOptions = [
    { value: '01', label: 'Januari' },
    { value: '02', label: 'Februari' },
    { value: '03', label: 'Maret' },
    { value: '04', label: 'April' },
    { value: '05', label: 'Mei' },
    { value: '06', label: 'Juni' },
    { value: '07', label: 'Juli' },
    { value: '08', label: 'Agustus' },
    { value: '09', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Desember' }
  ];


  const proofsByBillId = proofs.reduce((acc, proof) => {
    const current = acc[proof.bill_id];
    if (!current || new Date(proof.submitted_at) > new Date(current.submitted_at)) {
      acc[proof.bill_id] = proof;
    }
    return acc;
  }, {});

  const filteredBills = bills.filter((bill) => {
    if (bill.status === 'cancelled') return false;
    // Uploaded payments are handled exclusively in Verifikasi Pembayaran.
    if (bill.status !== 'paid' && bill.payment_status === 'pending_verification') return false;
    if (filterProperty !== 'all' && bill.property_name !== filterProperty) return false;
    if (filterMonth !== 'all' && getBillMonthKey(bill) !== filterMonth) return false;
    return true;
  });

  const unpaidBills = filteredBills.filter((bill) => bill.status === 'unpaid' && bill.payment_status !== 'pending_verification');
  const paidBills = filteredBills.filter((bill) => bill.status === 'paid');

  const availableMonthKeys = Array.from(
    new Set(
      bills
        .map((bill) => getBillMonthKey(bill))
        .filter(Boolean)
    )
  ).sort().reverse();

  const formatMonthOption = (value) => {
    if (!value) return value;
    const [year, month] = value.split('-');
    const monthLabel = monthOptions.find((option) => option.value === month)?.label || month;
    return `${monthLabel} ${year}`;
  };

  const renderBreakdown = (bill) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
      {bill.rent_amount > 0 && (
        <div className="p-2 bg-white/70 rounded-lg">
          <p className="text-isk-muted">Sewa</p>
          <p className="font-semibold">{formatCurrency(bill.rent_amount)}</p>
        </div>
      )}
      {bill.laundry_amount > 0 && (
        <div className="p-2 bg-white/70 rounded-lg">
          <p className="text-isk-muted">Laundry</p>
          <p className="font-semibold">{formatCurrency(bill.laundry_amount)}</p>
        </div>
      )}
      {bill.parking_amount > 0 && (
        <div className="p-2 bg-white/70 rounded-lg">
          <p className="text-isk-muted">Parkir</p>
          <p className="font-semibold">{formatCurrency(bill.parking_amount)}</p>
        </div>
      )}
      {bill.other_amount > 0 && (
        <div className="p-2 bg-white/70 rounded-lg">
          <p className="text-isk-muted">{bill.other_description || 'Lainnya'}</p>
          <p className="font-semibold">{formatCurrency(bill.other_amount)}</p>
        </div>
      )}
    </div>
  );

  const openProofPreview = (proof) => {
    if (!proof?.proof_image_url) return;
    setSelectedProofImage(resolveImg(proof.proof_image_url));
    setShowProofModal(true);
  };

  const closeProofPreview = () => {
    setShowProofModal(false);
    setSelectedProofImage('');
  };

  const renderBillRow = (bill, options = {}) => {
    const { tone = 'slate', statusLabel, statusClassName, proof, actions, detailFooter, onDelete } = options;
    const isExpanded = expandedBillId === bill.created_at;
    const toneClasses = {
      red: 'bg-red-50 border-red-200',
      yellow: 'bg-yellow-50 border-yellow-200',
      green: 'bg-green-50 border-green-200'
    };

    return (
      <div key={bill.created_at} className={`rounded-xl border ${toneClasses[tone]}`}>
        <button
          type="button"
          onClick={() => setExpandedBillId(isExpanded ? null : bill.created_at)}
          className="w-full p-4 text-left"
        >
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusClassName}`}>{statusLabel}</span>
                <span className="text-xs text-isk-muted">{bill.period}</span>
                <span className="text-xs text-isk-muted">{bill.property_name} - Kamar {bill.room_number}</span>
              </div>
              <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-isk-text truncate">{bill.tenant_name || tenantNameByPhone[bill.tenant_phone] || bill.tenant_phone}</p>
                  <p className="text-xs text-isk-muted">{bill.tenant_phone}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-isk-text">{formatCurrency(bill.total_amount)}</p>
                  <p className="text-xs text-isk-muted">
                    {proof ? `Dikirim tenant: ${formatDate(proof.submitted_at)}` : `Jatuh tempo: ${formatDate(bill.due_date)}`}
                  </p>
                </div>
              </div>
            </div>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-isk-muted flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-isk-muted flex-shrink-0" />}
          </div>
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 border-t border-white/40">
            <div className="pt-4 space-y-4">
              {renderBreakdown(bill)}

              {proof && (
                <div className="p-3 rounded-xl bg-white/70 border border-white/40">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <button
                      type="button"
                      onClick={() => openProofPreview(proof)}
                      className="w-full lg:w-44 h-32 rounded-lg overflow-hidden border border-white/40 bg-white"
                    >
                      <img
                        src={resolveImg(proof.proof_image_url)}
                        alt="Bukti pembayaran"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display='none'; e.target.parentElement.innerHTML='<div class="w-full h-full flex items-center justify-center text-xs text-red-400 p-2 text-center">File tidak tersedia</div>'; }}
                      />
                    </button>
                    <div className="flex-1 text-sm">
                      <p className="font-semibold text-isk-text">Detail Pembayaran</p>
                      <p className="text-isk-muted mt-1">Tanggal pembayaran: {formatDate(proof.submitted_at)}</p>
                      {proof.notes && <p className="text-isk-muted mt-1">Catatan tenant: {proof.notes}</p>}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openProofPreview(proof)}
                        className="mt-3 border-isk-teal/30 text-isk-teal"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Lihat Bukti
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {detailFooter}

              <div className="flex flex-wrap gap-3">
                {actions}
                {onDelete && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(bill.created_at)}
                    className="border-red-300 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Batalkan Tagihan
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-isk-muted">Memuat data...</p>
      </div>
    );
  }

  const statusFiltered = filteredBills.filter(b => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'unpaid') return b.status === 'unpaid' && b.payment_status !== 'pending_verification';
    if (filterStatus === 'paid') return b.status === 'paid';
    return true;
  });

  const statusCounts = {
    all: filteredBills.length,
    unpaid: filteredBills.filter(b => b.status === 'unpaid' && b.payment_status !== 'pending_verification').length,
    paid: filteredBills.filter(b => b.status === 'paid').length,
  };

  const getBillProof = (bill) => proofsByBillId[bill.created_at] || proofs.find(p => p.tenant_phone === bill.tenant_phone && p.bill_id === bill.created_at);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-bold text-isk-text">Tagihan Tenant</h2>
          <p className="text-sm text-isk-muted mt-0.5">Kelola tagihan bulanan tenant</p>
        </div>
        <Button
          onClick={() => openCreateModal()}
          className="bg-isk-teal hover:bg-isk-dark text-white rounded-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Buat Tagihan
        </Button>
      </div>

      {/* Messages */}
      {success && <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-isk-teal text-sm">{success}</div>}
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        {/* Status filter */}
        <div className="flex gap-1 bg-white/40 rounded-xl p-1">
          {[['all','Semua'],['unpaid','Belum Bayar'],['paid','Lunas']].map(([s, label]) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
              {label} ({statusCounts[s]})
            </button>
          ))}
        </div>
        {/* Property filter */}
        <Select value={filterProperty} onValueChange={setFilterProperty}>
          <SelectTrigger className="w-40 rounded-full text-xs h-8"><SelectValue placeholder="Semua Kost" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kost</SelectItem>
            {properties.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Month filter */}
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="w-36 rounded-full text-xs h-8"><SelectValue placeholder="Semua Bulan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Bulan</SelectItem>
            {availableMonthKeys.map(k => <SelectItem key={k} value={k}>{formatMonthOption(k)}</SelectItem>)}
          </SelectContent>
            </Select>
      </div>

      {/* Tabel tagihan */}
      <div className="glass-card rounded-2xl overflow-hidden border border-white/40">
        <div className="hidden sm:grid grid-cols-[2fr_1fr_1.5fr_1.5fr_1.5fr_1fr_auto] gap-3 px-4 py-2.5 bg-white/40 border-b border-white/40 text-xs font-semibold text-isk-muted">
          <span>Tenant</span><span>Kamar</span><span>Properti</span><span>Periode</span><span>Total</span><span>Status</span><span></span>
        </div>
        {statusFiltered.length === 0 && <div className="py-12 text-center text-isk-muted text-sm">Tidak ada tagihan</div>}
        <div className="divide-y divide-white/20">
          {statusFiltered.map(bill => {
            const proof = getBillProof(bill);
            const isPending = bill.payment_status === 'pending_verification';
            const isPaid = bill.status === 'paid';
            const statusLabel = isPaid ? 'Lunas' : isPending ? 'Menunggu Verif.' : 'Belum Bayar';
            const statusCls = isPaid ? 'bg-green-100 text-green-700' : isPending ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
            return (
              <div key={bill.created_at} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.5fr_1.5fr_1.5fr_1fr_auto] gap-2 sm:gap-3 px-4 py-3 items-center hover:bg-white/30 transition-colors">
                <div>
                  <p className="font-semibold text-isk-text text-sm">{bill.tenant_name || tenantNameByPhone[bill.tenant_phone] || bill.tenant_phone}</p>
                  <p className="text-xs text-isk-muted sm:hidden">{bill.property_name} · Kamar {bill.room_number} · {bill.period}</p>
                </div>
                <p className="hidden sm:block text-sm text-isk-text">{bill.room_number}</p>
                <p className="hidden sm:block text-xs text-isk-muted truncate">{bill.property_name}</p>
                <p className="hidden sm:block text-xs text-isk-text">{bill.period}</p>
                <p className="hidden sm:block text-sm font-semibold text-isk-teal">{formatCurrency(bill.total_amount)}</p>
                <div className="hidden sm:block"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>{statusLabel}</span></div>
                <Button size="sm" onClick={() => setSelectedBill({...bill, proof, statusLabel, statusCls})}
                  className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 transition-colors">
                  Detail <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail Popup */}
      {selectedBill && (() => {
        const bill = selectedBill;
        const proof = bill.proof;
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-bold text-isk-text text-lg">{bill.tenant_name || bill.tenant_phone}</h3>
                  <p className="text-xs text-isk-muted mt-0.5">{bill.property_name} · Kamar {bill.room_number}</p>
                  <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${bill.statusCls}`}>{bill.statusLabel}</span>
                </div>
                <button onClick={() => setSelectedBill(null)}><X className="w-5 h-5" /></button>
              </div>

              {/* Info tagihan */}
              <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                {[['Periode', bill.period], ['Jatuh Tempo', formatDate(bill.due_date)], ['Total', formatCurrency(bill.total_amount)]].map(([l,v]) => (
                  <div key={l} className="p-2 rounded-lg bg-gray-50">
                    <p className="text-isk-muted">{l}</p>
                    <p className="font-semibold text-isk-text">{v}</p>
                  </div>
                ))}
              </div>

              {/* Rincian */}
              <p className="text-xs font-bold text-isk-muted uppercase tracking-wide mb-2">Rincian</p>
              <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                {bill.rent_amount > 0 && <div className="p-2 bg-gray-50 rounded-lg"><p className="text-isk-muted">Sewa</p><p className="font-semibold">{formatCurrency(bill.rent_amount)}</p></div>}
                {bill.laundry_amount > 0 && <div className="p-2 bg-gray-50 rounded-lg"><p className="text-isk-muted">Laundry</p><p className="font-semibold">{formatCurrency(bill.laundry_amount)}</p></div>}
                {bill.parking_amount > 0 && <div className="p-2 bg-gray-50 rounded-lg"><p className="text-isk-muted">Parkir</p><p className="font-semibold">{formatCurrency(bill.parking_amount)}</p></div>}
                {bill.other_amount > 0 && <div className="p-2 bg-gray-50 rounded-lg"><p className="text-isk-muted">{bill.other_description || 'Lainnya'}</p><p className="font-semibold">{formatCurrency(bill.other_amount)}</p></div>}
              </div>

              {/* Bukti bayar */}
              {proof && (
                <div className="mb-4 p-3 rounded-xl bg-blue-50 border border-blue-100">
                  <p className="text-xs font-semibold text-isk-text mb-2">Bukti Pembayaran</p>
                  <p className="text-xs text-isk-muted">Dikirim: {formatDate(proof.submitted_at)}</p>
                  {proof.notes && <p className="text-xs text-isk-muted italic mt-1">"{proof.notes}"</p>}
                  <div className="mt-2">
                    {proofImgLoading ? (
                      <div className="flex items-center gap-2 text-xs text-isk-muted py-2">
                        <div className="w-4 h-4 border-2 border-isk-teal border-t-transparent rounded-full animate-spin" />
                        Memuat foto...
                      </div>
                    ) : proofImgUrl ? (
                      <>
                        <img
                          src={proofImgUrl}
                          alt="Bukti pembayaran"
                          className="w-full max-h-52 object-contain rounded-lg border border-blue-200 cursor-pointer"
                          onClick={() => { setSelectedProofImage(proofImgUrl); setShowProofModal(true); }}
                        />
                        <button
                          onClick={() => { setSelectedProofImage(proofImgUrl); setShowProofModal(true); }}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-isk-teal hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" /> Buka penuh
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-red-400">Foto tidak tersedia</p>
                    )}
                  </div>
                </div>
              )}

              {/* Aksi */}
              {userRole === 'admin' && bill.status === 'unpaid' && bill.payment_status !== 'pending_verification' && (
                <div className="mb-4">
                  {!showPaymentForm ? (
                    <Button onClick={() => setShowPaymentForm(true)}
                      className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full">
                      <CheckCircle className="w-4 h-4 mr-2" /> Sudah Bayar
                    </Button>
                  ) : (
                    <form onSubmit={handleSubmitPayment} className="p-4 rounded-xl bg-isk-teal/5 border border-isk-teal/20 space-y-3">
                      <p className="text-sm font-semibold text-isk-text">Kirim pembayaran ke verifikasi</p>
                      <div>
                        <Label htmlFor="admin-payment-date">Tanggal Pembayaran *</Label>
                        <Input id="admin-payment-date" type="date" value={paymentDate}
                          max={new Date().toISOString().slice(0, 10)}
                          onChange={e => setPaymentDate(e.target.value)} className="mt-1 rounded-xl" required />
                      </div>
                      <div>
                        <Label htmlFor="admin-payment-proof">Bukti Pembayaran *</Label>
                        <label htmlFor="admin-payment-proof" className="mt-1 flex items-center justify-center gap-2 rounded-xl border border-dashed border-isk-teal/40 bg-white px-3 py-4 text-sm text-isk-teal cursor-pointer hover:bg-isk-teal/5">
                          <Upload className="w-4 h-4" />
                          {paymentFile ? paymentFile.name : 'Pilih foto bukti pembayaran'}
                        </label>
                        <input id="admin-payment-proof" type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                          onChange={e => setPaymentFile(e.target.files?.[0] || null)} required />
                      </div>
                      <p className="text-xs text-isk-muted">Tagihan belum dianggap lunas sampai Super Admin menyetujui pembayaran.</p>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => { setShowPaymentForm(false); setPaymentDate(''); setPaymentFile(null); }}
                          className="flex-1 rounded-full">Batal</Button>
                        <Button type="submit" disabled={submittingPayment || !paymentDate || !paymentFile}
                          className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full disabled:opacity-50">
                          {submittingPayment ? 'Mengirim...' : 'Kirim ke Verifikasi'}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                {userRole === 'superadmin' && (
                  <Button onClick={() => { handleDeleteBill(bill.created_at); setSelectedBill(null); }}
                    variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 rounded-full text-sm">
                    <Trash2 className="w-4 h-4 mr-1" /> Batalkan Tagihan
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}


      {/* Create Bill Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-isk-text">Buat Tagihan Baru</h3>
              <button onClick={() => setShowModal(false)} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleCreateBill} className="space-y-4">
              {/* Step 1: Property */}
              <div className="space-y-2">
                <Label>1. Pilih Properti *</Label>
                <Select
                  value={modalProperty}
                  onValueChange={(v) => {
                    setModalProperty(v);
                    setModalPeriodMonth('');
                    setModalPeriodYear('');
                    setSelectedTenant(null);
                    setBillForm(prev => ({ ...prev, period: '', rent_amount: '' }));
                  }}
                >
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                    <SelectValue placeholder="Pilih properti" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 2: Period */}
              {modalProperty && (
                <div className="space-y-2">
                  <Label>2. Pilih Bulan Tagihan *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      value={modalPeriodMonth}
                      onValueChange={(v) => {
                        setModalPeriodMonth(v);
                        setSelectedTenant(null);
                        setBillForm(prev => ({ ...prev, rent_amount: '' }));
                      }}
                    >
                      <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                        <SelectValue placeholder="Bulan" />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={modalPeriodYear}
                      onValueChange={(v) => {
                        setModalPeriodYear(v);
                        setSelectedTenant(null);
                        setBillForm(prev => ({ ...prev, rent_amount: '' }));
                      }}
                    >
                      <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                        <SelectValue placeholder="Tahun" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map(y => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Step 3: Tenant */}
              {modalProperty && modalPeriodMonth && modalPeriodYear && (
                <div className="space-y-2">
                  <Label>3. Pilih Tenant *</Label>
                  {availableTenantsForBill.length === 0 ? (
                    <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-isk-teal text-sm">
                      Semua tenant di properti ini sudah ditagih untuk periode ini.
                    </div>
                  ) : (
                    <Select
                      value={selectedTenant?.tenant_phone || ''}
                      onValueChange={(value) => {
                        const tenant = availableTenantsForBill.find(t => t.tenant_phone === value);
                        setSelectedTenant(tenant);
                        if (tenant) {
                          setBillForm(prev => ({
                            ...prev,
                            period: `${modalPeriodYear}-${modalPeriodMonth}`,
                            rent_amount: tenant.room_price?.toString() || '',
                            laundry_amount: tenant.laundry && tenant.laundry_price > 0 ? tenant.laundry_price.toString() : '',
                            parking_amount: tenant.parkir && tenant.parkir_price > 0 ? tenant.parkir_price.toString() : '',
                            other_amount: tenant.cleaning && tenant.cleaning_price > 0 ? tenant.cleaning_price.toString() : '',
                            other_description: tenant.cleaning ? 'Kebersihan Kamar' : prev.other_description,
                          }));
                        }
                      }}
                    >
                      <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                        <SelectValue placeholder={`${availableTenantsForBill.length} tenant belum ditagih`}>
                          {selectedTenant ? `Kamar ${selectedTenant.room_number} — ${selectedTenant.tenant_name}` : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableTenantsForBill.map(t => (
                          <SelectItem key={t.tenant_phone} value={t.tenant_phone}>
                            Kamar {t.room_number} — {t.tenant_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {selectedTenant && (
                <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-sm">
                  <p className="font-semibold text-isk-text">{selectedTenant.tenant_name}</p>
                  <p className="text-isk-muted">{selectedTenant.property_name} — Kamar {selectedTenant.room_number}</p>
                </div>
              )}

              {/* Due Date */}
              <div className="space-y-2">
                <Label>Jatuh Tempo *</Label>
                <Input
                  type="date"
                  required
                  value={billForm.due_date}
                  onChange={(e) => setBillForm({ ...billForm, due_date: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                />
              </div>

              {/* Bill Items */}
              <div className="p-4 rounded-xl bg-white/50 space-y-3">
                <p className="font-semibold text-isk-text text-sm">Rincian Tagihan</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Sewa Kamar</Label>
                    <Input
                      type="number"
                      min="0"
                      value={billForm.rent_amount}
                      onChange={(e) => setBillForm({ ...billForm, rent_amount: e.target.value })}
                      placeholder="0"
                      className="glass-input border rounded-lg text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Laundry</Label>
                    <Input
                      type="number"
                      min="0"
                      value={billForm.laundry_amount}
                      onChange={(e) => setBillForm({ ...billForm, laundry_amount: e.target.value })}
                      placeholder="0"
                      className="glass-input border rounded-lg text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Parkir</Label>
                    <Input
                      type="number"
                      min="0"
                      value={billForm.parking_amount}
                      onChange={(e) => setBillForm({ ...billForm, parking_amount: e.target.value })}
                      placeholder="0"
                      className="glass-input border rounded-lg text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Lain-lain</Label>
                    <Input
                      type="number"
                      min="0"
                      value={billForm.other_amount}
                      onChange={(e) => setBillForm({ ...billForm, other_amount: e.target.value })}
                      placeholder="0"
                      className="glass-input border rounded-lg text-sm"
                    />
                  </div>
                </div>

                {parseFloat(billForm.other_amount || 0) > 0 && (
                  <div className="space-y-1">
                    <Label className="text-xs">Keterangan Lain-lain</Label>
                    <Input
                      type="text"
                      value={billForm.other_description}
                      onChange={(e) => setBillForm({ ...billForm, other_description: e.target.value })}
                      placeholder="Contoh: Listrik tambahan"
                      className="glass-input border rounded-lg text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="p-4 rounded-xl bg-isk-teal/10 border border-isk-teal/30">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-isk-text">TOTAL</span>
                  <span className="text-2xl font-bold text-isk-teal">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                >
                  Simpan Tagihan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showProofModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4"
          onClick={closeProofPreview}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeProofPreview}
              className="absolute -top-10 right-0 text-white hover:text-white/70"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="bg-slate-950 flex items-center justify-center p-4 min-h-[320px]">
                <img
                  src={selectedProofImage}
                  alt="Bukti pembayaran"
                  className="max-w-full max-h-[78vh] object-contain rounded-lg"
                  onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                />
                <div style={{display:'none'}} className="flex-col items-center gap-2 text-center">
                  <p className="text-red-400 font-medium">File tidak ditemukan di server</p>
                  <p className="text-gray-400 text-sm">File asli mungkin telah terhapus dari server</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillManagement;
